import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Loading } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ShieldCheck, Clock, Check, X, Crown, Wallet, MessageSquare } from "lucide-react";

export default function TutorAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tutors, setTutors] = useState(null);
  const [perms, setPerms] = useState(null);

  const load = () => api.get("/admin/tutors").then((r) => setTutors(r.data)).catch(() => setTutors([]));
  useEffect(() => {
    if (user && !user.is_owner) { navigate("/tutor", { replace: true }); return; }
    load();
    api.get("/settings/tutor-permissions").then((r) => setPerms({ payments: r.data.payments, communications: r.data.communications })).catch(() => setPerms({ payments: false, communications: false }));
  }, [user]);
  if (!user?.is_owner) return null;
  if (!tutors || !perms) return <Loading />;

  const togglePerm = async (key) => {
    const next = { ...perms, [key]: !perms[key] };
    setPerms(next);
    try {
      await api.put("/admin/tutor-permissions", next);
      toast.success(`${key === "payments" ? "Payments" : "Communications"} ${next[key] ? "enabled" : "hidden"} for tutors.`);
    } catch (err) {
      setPerms(perms);
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update access.");
    }
  };

  const act = async (t, action) => {
    try {
      await api.post(`/admin/tutors/${t.id}/${action}`);
      toast.success(action === "approve" ? `${t.name} approved — they can now sign in.` : `${t.name}'s application was declined.`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const pending = tutors.filter((t) => t.status === "pending");
  const active = tutors.filter((t) => t.status === "active");
  const rejected = tutors.filter((t) => t.status === "rejected");

  const Row = ({ t }) => (
    <div className="ember-card flex items-center gap-4 p-5" data-testid={`tutor-row-${t.id}`}>
      <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${t.is_owner ? "ember-gradient text-white" : "bg-[#EDE9FE] text-[#5B21B6]"}`}>
        {t.is_owner ? <Crown className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-heading font-bold text-[#1E1B2E]">{t.name}</span>
          {t.is_owner && <span className="rounded-full bg-[#EDE9FE] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#5B21B6]">Owner</span>}
        </div>
        <div className="truncate text-xs text-[#948EAB]">{t.email}</div>
      </div>
      {t.status === "pending" ? (
        <div className="flex gap-2">
          <Button size="sm" data-testid={`approve-${t.id}`} onClick={() => act(t, "approve")} className="h-9 rounded-xl ember-gradient text-xs font-bold"><Check className="mr-1 h-4 w-4" /> Approve</Button>
          <Button size="sm" variant="outline" data-testid={`reject-${t.id}`} onClick={() => act(t, "reject")} className="h-9 rounded-xl text-xs font-bold text-[#E11D48] hover:bg-[#FFF1F2]"><X className="mr-1 h-4 w-4" /> Decline</Button>
        </div>
      ) : t.status === "active" ? (
        <span className="rounded-full bg-[#ECFDF5] px-2.5 py-1 text-[11px] font-bold text-[#059669]">Active</span>
      ) : (
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-[#FEF2F2] px-2.5 py-1 text-[11px] font-bold text-[#E11D48]">Declined</span>
          <Button size="sm" variant="outline" data-testid={`reapprove-${t.id}`} onClick={() => act(t, "approve")} className="h-9 rounded-xl text-xs font-bold">Approve</Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="animate-fade-up" data-testid="tutor-admin">
      <PageHeader title="Tutors" subtitle="Approve tutor applications and manage who can access the portal." />

      <div className="ember-card mb-8 p-6" data-testid="tutor-access-card">
        <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-extrabold text-[#1E1B2E]">
          <ShieldCheck className="h-5 w-5 text-[#5B21B6]" /> Tutor access
        </h2>
        <p className="mb-4 text-sm text-[#948EAB]">Choose which owner-only areas your other tutors can see. These are hidden from tutors by default.</p>
        <div className="space-y-3">
          {[
            { key: "payments", label: "Payments", icon: Wallet, desc: "View fees, add/remove students and record payments." },
            { key: "communications", label: "Communications", icon: MessageSquare, desc: "See parent contacts and send SMS progress updates." },
          ].map((f) => (
            <div key={f.key} className="flex items-center justify-between rounded-xl border border-[#EEEAF8] p-4" data-testid={`perm-row-${f.key}`}>
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#EDE9FE] text-[#5B21B6]"><f.icon className="h-5 w-5" /></span>
                <div>
                  <div className="font-bold text-[#1E1B2E]">{f.label}</div>
                  <div className="text-xs text-[#948EAB]">{f.desc}</div>
                </div>
              </div>
              <Switch data-testid={`perm-toggle-${f.key}`} checked={perms[f.key]} onCheckedChange={() => togglePerm(f.key)} />
            </div>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-extrabold text-[#1E1B2E]">
          <Clock className="h-5 w-5 text-[#B45309]" /> Pending approval
          {pending.length > 0 && <span className="rounded-full bg-[#FFFBEB] px-2 py-0.5 text-xs font-bold text-[#B45309]">{pending.length}</span>}
        </h2>
        {pending.length === 0 ? (
          <div className="ember-card py-10 text-center text-sm text-[#948EAB]">No tutor applications waiting for review.</div>
        ) : (
          <div className="grid gap-3">{pending.map((t) => <Row key={t.id} t={t} />)}</div>
        )}
      </div>

      <div className="mb-8">
        <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-extrabold text-[#1E1B2E]"><ShieldCheck className="h-5 w-5 text-[#059669]" /> Active tutors</h2>
        <div className="grid gap-3">{active.map((t) => <Row key={t.id} t={t} />)}</div>
      </div>

      {rejected.length > 0 && (
        <div>
          <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-extrabold text-[#1E1B2E]"><X className="h-5 w-5 text-[#E11D48]" /> Declined</h2>
          <div className="grid gap-3">{rejected.map((t) => <Row key={t.id} t={t} />)}</div>
        </div>
      )}
    </div>
  );
}
